import React, { useState, useEffect } from 'react';
import { Input, Button, Select, Tag, Modal, Card, Avatar, Table, message } from 'antd';
import { SearchOutlined, CheckCircleOutlined, MessageOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../services/axios';

const { TextArea } = Input;
const { Option } = Select;

const ResolveTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [loading, setLoading] = useState(false);


  const priorityColors = {
    low: 'green',
    medium: 'orange',
    high: 'red'
  };

  const fetchTicketsForResolve = async () => {
    try {
      const res = await axios.post('/fetch-ticket-forResolve');

      const formatted = res.data.data.map(t => ({
        ...t,
        id: t.code
      }));

      setTickets(formatted);

    } catch (error) {
      console.error("Failed to fetch resolve tickets:", error);
      message.error("Unable to load tickets");
    }
  };

  useEffect(() => {
    fetchTicketsForResolve();
  }, []);

  const filteredTickets = tickets.filter(ticket => {
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'open' && !ticket.isresolved) ||
      (filterStatus === 'closed' && ticket.isresolved)

    const matchesSearch =
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.code.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesPriority && matchesStatus && matchesSearch;
  });

  const handleResolveTicket = async () => {
    if (!resolutionNote.trim()) {
      message.warning("Please provide resolution note");
      return;
    }

    setLoading(true);

    try {
      await axios.post("/resolve-tickets", {
        ticketId: selectedTicket.code,
        resolution: resolutionNote
      });

      message.success("Ticket resolved successfully");

      // close modal
      setIsResolveModalOpen(false);
      setResolutionNote("");

      // refresh list
      fetchTicketsForResolve();

    } catch (error) {
      console.error(error);
      message.error("Resolve failed");
    } finally {
      setLoading(false);
    }
  };

  const showResolveConfirm = (ticket) => {
    setSelectedTicket(ticket);
    setIsResolveModalOpen(true);
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const formatDateTime = (isoString) => {
    return dayjs(isoString).format('DD-MMM-YYYY HH:mm');
  };

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <span className="font-semibold text-blue-600">{id}</span>,
      width: 120,
      fixed: 'left'
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <span className="font-medium">{text}</span>,
      width: 200
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'isresolved',
      render: (value) =>
        value ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>CLOSED</Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />}>OPEN</Tag>
        ),
      width: 110
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (level) => (
        <Tag color={priorityColors[level]}>{level.toUpperCase()}</Tag>
      ),
      width: 100
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag color="blue">{cat}</Tag>,
      width: 120
    },
    {
      title: 'Created By',
      dataIndex: 'username',
      key: 'username',
      render: (name) => (
        <div className="flex items-center gap-2">
          <Avatar className={getAvatarColor(name)} size="small">
            {getInitials(name)}
          </Avatar>
          <span className="text-sm">{name}</span>
        </div>
      ),
      width: 180
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => dayjs(date).format('DD-MM-YYYY'),
      width: 120
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => {
        const isOverdue = dayjs(date).isBefore(dayjs()) && !tickets.find(t => t.dueDate === date)?.isresolved;
        return (
          <span className={isOverdue ? 'text-red-500 font-semibold' : ''}>
            {dayjs(date).format('DD-MM-YYYY')}
          </span>
        );
      },
      width: 120
    },
    {
      title: 'Comments',
      key: 'comments',
      render: (_, row) => (
        <span className="flex items-center gap-1">
          <MessageOutlined /> {row.comments.length}
        </span>
      ),
      width: 100
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTicket(record);
              setIsModalOpen(true);
            }}
          >
            View
          </Button>
          {!record.isresolved && (
            <Button
              type="primary"
              size="small"
              className="bg-green-600 hover:bg-green-700"
              onClick={(e) => {
                e.stopPropagation();
                showResolveConfirm(record);
              }}
            >
              Resolve
            </Button>
          )}
        </div>
      )
    }
  ];

  const openTickets = tickets.filter(t => !t.isresolved).length;
  const closedTickets = tickets.filter(t => t.isresolved).length;
  const highPriorityOpen = tickets.filter(
    t => t.priority === 'high' && !t.isresolved
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Ticket Management
          </h1>
          <p className="text-gray-600">Manage and resolve support tickets</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card className="shadow-sm border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Open Tickets</p>
                <p className="text-2xl font-bold text-orange-600">{openTickets}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <ClockCircleOutlined className="text-2xl text-orange-500" />
              </div>
            </div>
          </Card>

          <Card className="shadow-sm border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Closed Tickets</p>
                <p className="text-2xl font-bold text-green-600">{closedTickets}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <CheckCircleOutlined className="text-2xl text-green-500" />
              </div>
            </div>
          </Card>

          <Card className="shadow-sm border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">High Priority Open</p>
                <p className="text-2xl font-bold text-red-600">{highPriorityOpen}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <ExclamationCircleOutlined className="text-2xl text-red-500" />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 shadow-sm border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input
              placeholder="Search by title or ID..."
              prefix={<SearchOutlined className="text-gray-400" />}
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Select
              value={filterStatus}
              size="large"
              onChange={setFilterStatus}
              className="w-full"
            >
              <Option value="all">All Status</Option>
              <Option value="open">Open</Option>
              <Option value="closed">Closed</Option>
            </Select>

            <Select
              value={filterPriority}
              size="large"
              onChange={setFilterPriority}
              className="w-full"
            >
              <Option value="all">All Priority</Option>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
            </Select>
          </div>
        </Card>

        {/* Tickets Table */}
        <Card className="shadow-sm">
          <Table
            columns={columns}
            dataSource={filteredTickets}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
            rowClassName={(record) => 
              !record.isresolved ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'
            }
          />
        </Card>

        {filteredTickets.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No tickets found</p>
          </div>
        )}
      </div>

      {/* View Ticket Modal */}
      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        {selectedTicket && (
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Tag color={priorityColors[selectedTicket.priority]}>
                    {selectedTicket.priority.toUpperCase()}
                  </Tag>
                  <Tag color="blue">{selectedTicket.category}</Tag>
                  {selectedTicket.isresolved ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>CLOSED</Tag>
                  ) : (
                    <Tag color="orange" icon={<ClockCircleOutlined />}>OPEN</Tag>
                  )}
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">
                  {selectedTicket.title}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {selectedTicket.description}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Created By</p>
                <div className="flex items-center gap-2">
                  <Avatar className={getAvatarColor(selectedTicket.username)} size="small">
                    {getInitials(selectedTicket.username)}
                  </Avatar>
                  <span className="font-medium text-gray-800 text-sm">
                    {selectedTicket.username}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Created On</p>
                <span className="font-medium text-gray-800 text-sm">
                  {formatDateTime(selectedTicket.createdOn)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Due Date</p>
                <span className="font-medium text-gray-800 text-sm">
                  {dayjs(selectedTicket.dueDate).format('DD-MM-YYYY')}
                </span>
              </div>
              {selectedTicket.isResolved && (
                <>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Resolved By</p>
                    <span className="font-medium text-green-700 text-sm">
                      {selectedTicket.resolvedBy}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 mb-1">Resolution</p>
                    <p className="text-sm text-gray-700 bg-green-50 p-3 rounded border border-green-200">
                      {selectedTicket.resolution}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <MessageOutlined /> Comments ({selectedTicket.comments.length})
              </h3>
              {selectedTicket.comments.length === 0 ? (
                <p className="text-gray-500 text-sm">No comments yet</p>
              ) : (
                <div className="space-y-3">
                  {selectedTicket.comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className={getAvatarColor(comment.user)} size="small">
                          {getInitials(comment.user)}
                        </Avatar>
                        <span className="font-semibold text-sm">{comment.user}</span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(comment.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!selectedTicket.isResolved && (
              <div className="mt-6 pt-4 border-t flex justify-end">
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  size="large"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setIsModalOpen(false);
                    showResolveConfirm(selectedTicket);
                  }}
                >
                  Resolve Ticket
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Resolve Ticket Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CheckCircleOutlined className="text-green-600" />
            <span className="text-xl font-semibold">Resolve Ticket</span>
          </div>
        }
        open={isResolveModalOpen}
        onCancel={() => {
          setIsResolveModalOpen(false);
          setResolutionNote('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsResolveModalOpen(false);
            setResolutionNote('');
          }}>
            Cancel
          </Button>,
          <Button
            key="resolve"
            type="primary"
            loading={loading}
            onClick={handleResolveTicket}
            className="bg-green-600 hover:bg-green-700"
            icon={<CheckCircleOutlined />}
          >
            Mark as Resolved
          </Button>
        ]}
        width={600}
      >
        {selectedTicket && (
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">Ticket ID</p>
              <p className="font-semibold text-blue-600">{selectedTicket.id}</p>
              <p className="text-sm text-gray-600 mt-2 mb-1">Title</p>
              <p className="font-medium text-gray-800">{selectedTicket.title}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Details *
              </label>
              <TextArea
                placeholder="Describe how the ticket was resolved, what actions were taken, and any relevant details..."
                rows={6}
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-2">
                Please provide detailed information about the resolution
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ResolveTickets;